// components/CategoryAutocomplete.jsx - CORREGIDO (muestra todas las categorías al hacer click)
import React, { useState, useEffect, useRef } from 'react';

const CategoryAutocomplete = ({
  value = '',
  onChange,
  categories = [],
  placeholder = 'Select or type category...',
  disabled = false,
  isLoading = false,
  dropdownPosition = 'bottom'
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Formatear categoría para display
  const formatCategory = (category) => {
    if (!category) return '';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };

  // Actualizar input cuando cambia el valor prop
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Filtrar categorías cuando cambia el input
  useEffect(() => {
    const trimmedInput = inputValue.trim().toLowerCase();
    
    if (!trimmedInput) {
      setFilteredCategories([...categories]);
      return;
    }

    // 🟢 NUEVA CONDICIÓN: Si hay coincidencia exacta, NO mostrar dropdown
    const exactMatch = categories.some(
      category => category.toLowerCase() === trimmedInput
    );
    
    if (exactMatch) {
      setFilteredCategories([]);  // Vacío para que no se muestre dropdown
      return;
    }

    const filtered = categories.filter(category => 
      category.toLowerCase().includes(trimmedInput)
    );

    setFilteredCategories(filtered);
    
  }, [inputValue, categories]);

  // Manejar clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowDropdown(true);
    
    // Llamar onChange en cada cambio
    onChange(newValue);
  };

  const handleSelectCategory = (category) => {
    setInputValue(category);
    onChange(category);
    setShowDropdown(false);
    
    // Enfocar input después de seleccionar
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
      }
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (inputValue.trim() && filteredCategories.length > 0) {
        handleSelectCategory(filteredCategories[0]);
      } else {
        onChange(inputValue.trim());
        setShowDropdown(false);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    setShowDropdown(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Determinar si se debe mostrar dropdown - AHORA SIEMPRE cuando showDropdown es true
  const shouldShowDropdown = showDropdown && !disabled;

  // Obtener las categorías a mostrar (todas cuando está vacío, filtradas cuando hay input)
  const categoriesToShow = inputValue.trim() === '' 
    ? [...categories]  // Todas las categorías cuando está vacío
    : filteredCategories;  // Categorías filtradas cuando hay input

  // Estilos para posición del dropdown
  const getDropdownStyles = () => {
    if (dropdownPosition === 'top') {
      return {
        bottom: '100%',
        top: 'auto',
        marginBottom: '8px',
        marginTop: '0'
      };
    }
    return {
      top: '100%',
      bottom: 'auto',
      marginTop: '8px',
      marginBottom: '0'
    };
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Input principal */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        
        {/* Botón clear */}
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
            aria-label="Clear"
          >
            <i className="fas fa-times text-gray-600 text-xs"></i>
          </button>
        )}
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
          </div>
        )}
      </div>

      {/* Dropdown - Ahora se muestra incluso cuando está vacío */}
      {shouldShowDropdown && categoriesToShow.length > 0 && (
        <div 
          className="absolute z-[9999] w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
          style={getDropdownStyles()}
        >
          <div className="py-1">
            {categoriesToShow.map(category => (
              <button
                key={category}
                type="button"
                onClick={() => handleSelectCategory(category)}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
              >
                {formatCategory(category)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryAutocomplete;